import React, { useCallback, useEffect, useRef, useState } from "react";
import { styled } from "../stitches.config";
import chroma from "chroma-js";
import {
  pixelToGridPosition,
  gridToPixelPosition,
  pixelToGridSize,
  gridToPixelSize,
} from "../utils/gridPosition";
import { AnyBlock, BlockType, Point2D, Position, Size } from "../types";
import createBlock from "./createBlock";
import { DEFAULT_GRID_RESOLUTION, GRID_SEAM } from "../consts";
import { useAtom } from "jotai";
import {
  currentUserAtom,
  useMouseRef,
  useCanvasRef,
  blockYouJustCreatedAtom,
} from "../jotai/atoms";
import { MapClient } from "@roomservice/react";
import { useBlockIds, useUserInfo } from "../roomservice/maps";
import { useRoomClient } from "../roomservice/utils";
import { useUpdateAtom } from "jotai/utils";
import { usePreviews } from "../roomservice/presences";
const INITIAL_SIZE: Size = { width: 1, height: 1 };

export function previewBigEnough(size: Size) {
  return size.width >= 2 && size.height >= 2;
}

function BlockPreview({
  upsertBlock,
}: {
  upsertBlock: (block: AnyBlock) => void;
}) {
  const [currentUser] = useAtom(currentUserAtom);
  const [, blockIdsClient] = useBlockIds();
  const room = useRoomClient();
  const [, previewClient] = usePreviews();

  const canvasRef = useCanvasRef();
  const mouseRef = useMouseRef();

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [mouse, setMouse] = useState<Position>();
  const [start, setStart] = useState<Position>();
  const [size, setSize] = useState<Size>(INITIAL_SIZE);
  const startRef = useRef<Position>();
  const sizeRef = useRef<Size>();
  const isDraggingRef = useRef<boolean>(false);
  startRef.current = start;
  sizeRef.current = size;
  isDraggingRef.current = isDragging;

  // Search for another preview block made by the same user and clear them
  const deleteOtherPreviewBlock = useCallback(
    function () {
      // 1+N Query - is there a smarter way to filter or cache this data?
      for (const blockId in blockIdsClient?.toObject()) {
        if (!room)
          throw new Error("RoomClient missing, this should never happen.");

        const b: MapClient<AnyBlock> = room.map(blockId);
        const { type, madeBy, updatedAt } = b?.toObject();

        // hack to use age to avoid deleting the block you just made
        let age = Date.now() - updatedAt;
        if (type === undefined && madeBy === currentUser?.id && age > 500) {
          console.log("Deleting your old preview block");
          return blockIdsClient?.delete(blockId);
        }
      }
    },
    [currentUser, room, blockIdsClient]
  );
  let clearPreviewPresence = useCallback(() => {
    previewClient!.set(
      {
        position: { x: -100, y: -100 },
        size: { width: 0, height: 0 },
      },
      60 * 2
    );
  }, [previewClient]);

  // send current preview rectangle
  useEffect(() => {
    if (!previewClient) return;
    if (start && isDragging) {
      previewClient!.set(
        {
          position: gridToPixelPosition(start),
          size: gridToPixelSize(size),
        },
        60 * 2 //two minutes?
      );
    } else {
      clearPreviewPresence();
    }
  }, [start, size, previewClient, clearPreviewPresence, isDragging]);

  const bigEnough = previewBigEnough(sizeRef.current);
  const isText =
    bigEnough && (sizeRef.current.height <= 4 || sizeRef.current.width <= 4);
  let setBlockYouJustCreated = useUpdateAtom(blockYouJustCreatedAtom);

  useEffect(() => {
    if (!canvasRef || !mouseRef?.current) return;
    function onClick(e: MouseEvent) {
      if (e.currentTarget === e.target) {
        deleteOtherPreviewBlock();
      }
    }

    function onMouseMove(e: MouseEvent) {
      setMouse(mouseRef!.current);
      if (isDraggingRef.current && startRef.current) {
        e.preventDefault();
        const startPix = gridToPixelPosition(startRef.current);
        setSize(
          pixelToGridSize(
            {
              width: mouseRef!.current.x - startPix.x,
              height: mouseRef!.current.y - startPix.y,
            },
            true
          )
        );
      }
    }

    function onMouseDown(e: MouseEvent) {
      if (e.currentTarget !== e.target) return;
      setIsDragging(true);
      setStart(pixelToGridPosition(mouseRef!.current));
    }

    function onMouseUp(e: MouseEvent) {
      setIsDragging(false);

      if (bigEnough) {
        deleteOtherPreviewBlock();
        let type: BlockType = undefined;
        if (isText) {
          type = "text";
        }

        const block = createBlock(type, startRef.current, sizeRef.current);
        if (block) {
          upsertBlock(block);
          setBlockYouJustCreated(block.id);

          // hack, double check that we clear the preview presence
          window.setTimeout(clearPreviewPresence, 40);
        }
      }
      setStart(undefined);
      setSize(INITIAL_SIZE);
    }

    canvasRef.addEventListener("click", onClick);
    canvasRef.addEventListener("mousemove", onMouseMove);
    canvasRef.addEventListener("mouseover", onMouseMove);
    canvasRef.addEventListener("mousedown", onMouseDown);
    canvasRef.addEventListener("mouseup", onMouseUp);

    return () => {
      canvasRef.removeEventListener("click", onClick);
      canvasRef.removeEventListener("mousemove", onMouseMove);
      canvasRef.removeEventListener("mouseover", onMouseMove);
      canvasRef.removeEventListener("mousedown", onMouseDown);
      canvasRef.removeEventListener("mouseup", onMouseUp);
    };
  }, [
    canvasRef,
    mouseRef,
    setMouse,
    upsertBlock,
    isDraggingRef,
    bigEnough,
    currentUser,
    deleteOtherPreviewBlock,
    setBlockYouJustCreated,
    isText,
    clearPreviewPresence,
  ]);
  if (!mouse) return <></>;

  const position = gridToPixelPosition(
    start ? start : pixelToGridPosition(mouse)
  );

  return (
    <BlockPreviewRect
      isDragging={isDragging}
      bigEnough={bigEnough}
      isText={isText}
      position={position}
      size={size}
      userId={currentUser!.id}
    />
  );
}

export function BlockPreviewRect({
  isDragging,
  bigEnough,
  isText,
  position,
  size,
  userId,
}: {
  isDragging: boolean;
  bigEnough: boolean;
  isText: boolean;
  position: Point2D;
  size: Size;
  userId: string;
}) {
  let [user] = useUserInfo(userId);
  const color = user?.color || "rgba(0,0,0,0)";

  const seamStyle = {
    display: isDragging ? "block" : "none",
    transform: `translate(${position.x}px, ${position.y}px)`,
    width: `${Math.max(size.width, 1) * DEFAULT_GRID_RESOLUTION}px`,
    height: `${Math.max(size.height, 1) * DEFAULT_GRID_RESOLUTION}px`,
  };

  const style = {
    backgroundColor: bigEnough
      ? chroma(color).alpha(0.35).css()
      : "rgba(0,0,0,.05)",
    backgroundImage: bigEnough
      ? isText
        ? `url("/textPattern.svg")`
        : 'url("/pluspattern.svg")'
      : "none",
    borderColor: bigEnough ? color : "rgba(0,0,0,0.02)",
  };

  return (
    <Seam style={seamStyle}>
      <Preview style={style}></Preview>
    </Seam>
  );
}
const Seam = styled("div", {
  padding: GRID_SEAM / 2,
  display: "grid",
  willChange: "transform",
  position: "absolute",
  top: 0,
  left: 0,
  zIndex: "$blockPreview",
});
export default BlockPreview;

const Preview = styled("div", {
  backgroundPosition: `${GRID_SEAM}px ${GRID_SEAM}px`,
  height: "100%",
  width: "100%",
  pointerEvents: "none",
  borderWidth: "1px",
  borderStyle: "solid",
  transition: "background-color 0.01s backgroundColor 0.01s",
  boxShadow: "1px 1px 10px rgba(0, 0, 0, .1)",
  backgroundSize: `${DEFAULT_GRID_RESOLUTION}px ${DEFAULT_GRID_RESOLUTION}px`,
});
